import React, { useState, useEffect } from "react";
import { Modal, Alert, Form, Row, Col } from "react-bootstrap";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { payTax } from "../../store/slices/payrollSlice";
import info_icon from "../../assets/images/info_icon.svg";
import { formatDatePopup } from "../../utils/dateUtils";
import TaxPaymentSuccessModal from "./TaxPaymentSuccessModal";

const IRS_TAX_TYPE = 1;

const TaxPaymentModal = ({
  show,
  onHide,
  payrollHeaderId,
  companyId,
  payrollFrom,
  payrollTo,
  companyTaxScheduleType,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.user);

  // =============================
  // STATES
  // =============================

  const [payrollType, setPayrollType] = useState("quarterly"); // quarterly | single

  // Deposit type is derived from the company's tax schedule setting and cannot be changed.
  const taxDepositType =
    String(companyTaxScheduleType) === "2" ? "monthly" : "immediate";

  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const taxYear = payrollFrom
    ? new Date(payrollFrom).getFullYear()
    : new Date().getFullYear();

  // =============================
  // RESET ON OPEN
  // =============================

  useEffect(() => {
    if (show) {
      setPayrollType("quarterly");
      setSelectedQuarter(null);
      setConfirmChecked(false);
      setError("");

      setDateRange({
        from: payrollFrom?.split("T")[0] || "",
        to: payrollTo?.split("T")[0] || "",
      });
    }
  }, [show, payrollFrom, payrollTo]);

  // =============================
  // QUICK DATE SELECT
  // =============================

  const handleQuickSelect = (type) => {
    const today = new Date();

    if (type === "this-week") {
      const monday = new Date(today);
      monday.setDate(today.getDate() - today.getDay() + 1);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      setDateRange({
        from: monday.toISOString().split("T")[0],
        to: sunday.toISOString().split("T")[0],
      });
    }

    if (type === "last-week") {
      const monday = new Date(today);
      monday.setDate(today.getDate() - today.getDay() + 1 - 7);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      setDateRange({
        from: monday.toISOString().split("T")[0],
        to: sunday.toISOString().split("T")[0],
      });
    }

    if (type === "current-month") {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      setDateRange({
        from: formatDatePopup(firstDay),
        to: formatDatePopup(lastDay),
      });
    }
  };

  const canConfirm =
    confirmChecked &&
    ((payrollType === "quarterly" && selectedQuarter) ||
      (payrollType === "single" && dateRange.from && dateRange.to)) &&
    (taxDepositType === "immediate" || taxDepositType === "monthly");

  const handleConfirm = async () => {
    setLoading(true);
    setError("");

    try {
      const depositTypeEnum = taxDepositType === "immediate" ? 1 : 2;
      const request = {
        companyId,
        payrollHeaderId,
        taxType: IRS_TAX_TYPE,
        taxYear,
        payrollType,
        depositScheduleType: depositTypeEnum,
      };

      if (payrollType === "quarterly") {
        request.quarter = selectedQuarter;
      }

      if (payrollType === "single") {
        request.fromDate = dateRange.from;
        request.toDate = dateRange.to;
      }

      await dispatch(
        payTax({ request, userId: user.id, key: "payTax" }),
      ).unwrap();

      setShowSuccessModal(true);
      onHide?.();
    } catch (e) {
      setError(e?.error?.join(", ") || e?.message || "Failed to pay tax");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    onSuccess?.();
  };

  // =============================
  // UI
  // =============================

  return (
    <>
      <Modal show={show} onHide={onHide} centered className="tax_payment_modal">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Pay Tax</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* ============================= */}
          {/* Payroll Type Toggle */}
          {/* ============================= */}

          <div className="tax-toggle">
            <button
              className={`tax-toggle-btn ${payrollType === "quarterly" ? "active" : ""}`}
              onClick={() => setPayrollType("quarterly")}
            >
              Quarter-wise
            </button>

            <button
              className={`tax-toggle-btn ${payrollType === "single" ? "active" : ""}`}
              onClick={() => setPayrollType("single")}
            >
              Single Payroll
            </button>
          </div>

          {/* ============================= */}
          {/* Deposit Type Radio */}
          {/* ============================= */}

          <div className="mb-4 mt-3">
            <label className="section-label">Tax Deposit Options</label>

            <div className="radio-group">
              <div className="form-check">
                <input
                  type="radio"
                  id="immediate"
                  name="taxDepositType"
                  className="form-check-input"
                  checked={taxDepositType === "immediate"}
                  disabled
                  readOnly
                />
                <label htmlFor="immediate">Immediate Tax Deposits</label>
              </div>

              <div className="form-check">
                <input
                  type="radio"
                  id="monthly"
                  name="taxDepositType"
                  className="form-check-input"
                  checked={taxDepositType === "monthly"}
                  disabled
                  readOnly
                />
                <label htmlFor="monthly">Monthly Tax Depositors</label>
              </div>
            </div>
          </div>

          {/* ============================= */}
          {/* Payroll Scope Section */}
          {/* ============================= */}

          {payrollType === "quarterly" && (
            <div className="mb-4">
              <label className="section-label">
                Applicable Quarters ({taxYear})
              </label>

              <div className="quarter-pill-group">
                {[1, 2, 3, 4].map((q) => (
                  <button
                    key={q}
                    className={`quarter-pill ${selectedQuarter === q ? "selected" : ""}`}
                    onClick={() =>
                      setSelectedQuarter(selectedQuarter === q ? null : q)
                    }
                  >
                    Q{q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {payrollType === "single" && (
            <div className="mb-4">
              <label className="section-label">Select Payroll Range</label>

              <Row className="g-2 mb-2">
                <Col>
                  <Form.Control
                    type="date"
                    value={dateRange.from}
                    onChange={(e) =>
                      setDateRange({
                        ...dateRange,
                        from: e.target.value,
                      })
                    }
                  />
                </Col>

                <Col xs="auto" className="align-self-center">
                  to
                </Col>

                <Col>
                  <Form.Control
                    type="date"
                    value={dateRange.to}
                    onChange={(e) =>
                      setDateRange({
                        ...dateRange,
                        to: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>

              <div className="quick-chips">
                <button
                  onClick={() => handleQuickSelect("this-week")}
                  className="chip"
                >
                  This Week
                </button>
                <button
                  onClick={() => handleQuickSelect("last-week")}
                  className="chip"
                >
                  Last Week
                </button>
                <button
                  onClick={() => handleQuickSelect("current-month")}
                  className="chip"
                >
                  Current Month
                </button>
              </div>
            </div>
          )}

          {/* ============================= */}
          {/* Info Box */}
          {/* ============================= */}

          <div className="tax-warning-box">
            <img src={info_icon} width="20" alt="info" />
            <p className="mb-0">
              Please ensure tax has been paid externally before confirming. This
              action updates your records and cannot be undone.
            </p>
          </div>

          {/* Confirm */}
          <div className="confirm-check mb-3">
            <input
              type="checkbox"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
            />
            <label>I verify that tax for selected period has been paid.</label>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
        </Modal.Body>

        <Modal.Footer className="border-0">
          <button className="btn-cancel" onClick={onHide}>
            Cancel
          </button>

          <button
            className="btn-confirm"
            disabled={!canConfirm || loading}
            onClick={handleConfirm}
          >
            {loading ? "Processing..." : "Confirm Payment"}
          </button>
        </Modal.Footer>
      </Modal>

      <TaxPaymentSuccessModal
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
      />
    </>
  );
};

export default TaxPaymentModal;
